import React, { useState } from 'react';
import { Autocomplete } from '@material-ui/lab';
import { Grow, InputAdornment, Popper, TextField } from '@material-ui/core';
import './AutocompleteMui.scss';
import { ReactComponent as RemoveIcon } from './icons/remove.svg';
import SearchIcon from '@material-ui/icons/Search';
import IconButton from '@material-ui/core/IconButton';
import PropTypes from 'prop-types';
import { ReactComponent as ErrorIcon } from '../../assets/icons/error.svg';
import { useTranslation } from 'react-i18next';
import AvatarUser from '../../assets/images/avatars/avatar-users.png';
import AvatarSpec from '../../assets/images/avatars/avatar-spectator.png';
import { ReactComponent as ArrowDownIcon } from '../../assets/icons/select-arrow.svg';

const AutocompleteMUI = ({
                             className,
                             text,
                             array,
                             onChange,
                             error,
                             value = [],
                             disabled = false,
                             withoutAvatars = false,
                             maxItems = 999,
                         }) => {
    const [open, setOpen] = useState(false);
    const { t } = useTranslation();

    const disabledKeyboard = typeof window !== 'undefined' && window?.innerWidth <= 768;

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const selectedTags = value.map((option, index) => {
        const label = option.label;
        const isMe = option?.isMe ?? false;
        return (
            <div className={`autocomplete-chip${disabled || isMe ? ' disabled' : ''}`} key={index}>
                {!withoutAvatars && (
                    <img
                        className="autocomplete-chip__image"
                        src={option?.avatar ? option.avatar : option.isSpectator ? AvatarSpec : AvatarUser}
                        alt="avatar"
                    />
                )}
                <p className="autocomplete-chip__title">{label}</p>
                {!disabled && !isMe ? (
                    <RemoveIcon
                        className="autocomplete-chip__remove"
                        onClick={() => {
                            const newVal = value.filter((entry) => entry !== option);
                            onChange(newVal);
                        }}
                    />
                ) : null}
            </div>
        );
    });

    const CustomPopper = ({ anchorEl, open, placement, children, ...props }) => {
        return (
            <Popper anchorEl={anchorEl} open={open} placement={placement} transition {...props}>
                {({ TransitionProps }) => (
                    <Grow {...TransitionProps} style={{ transformOrigin: 'center top' }}>
                        <div className="autocomplete-popper">{children}</div>
                    </Grow>
                )}
            </Popper>
        );
    };

    return (
        <>
            <Autocomplete
                filterSelectedOptions
                options={array}
                onChange={(e, newValue) => {
                    onChange(newValue);
                    handleClose();
                }}
                autoHighlight
                variant="outlined"
                className={className}
                PopperComponent={CustomPopper}
                margin="normal"
                value={value}
                disabled={disabled || (maxItems ? value?.length === maxItems : undefined)}
                multiple
                getOptionLabel={(option) => option.label}
                getOptionSelected={(option, value) => option.id === value.id}
                open={open}
                onOpen={handleOpen}
                onClose={handleClose}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder={text}
                        variant="outlined"
                        classes={{ root: 'input-mui' }}
                        className={className}
                        InputProps={{
                            ...params.InputProps,
                            autoComplete: 'new-password',
                            readOnly: disabledKeyboard ?? false,
                            startAdornment: (
                                <InputAdornment position="start">
                                    <IconButton onClick={handleOpen}>
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                            endAdornment: open ? (
                                <IconButton onClick={handleClose}>
                                    <RemoveIcon />
                                </IconButton>
                            ) : null,
                        }}
                        InputLabelProps={{
                            classes: {
                                root: 'input-mui__label',
                                focused: 'input-mui__label--focused',
                                shrink: 'input-mui__label--shrink',
                                error: 'input-mui__label--error',
                                disabled: 'input-mui__label--disabled',
                            },
                            shrink: true,
                        }}
                    />
                )}
                renderOption={(props, option) => {
                    return (
                        <ul {...props} key={option.code}>
                            <li className={'autocomplete__li'}>
                                <div className={'autocomplete__item'}>
                                    {!withoutAvatars && <div className='autocomplete__img'>
                                        <img
                                            src={props.avatar ? props.avatar : (props.isSpectator ? AvatarSpec : AvatarUser)}
                                            alt={props.label}/>
                                    </div>}
                                    <div className='autocomplete__details'>
                                        {props.label && <p> {props.label}</p>}
                                        {props.text && <span> {props.text}</span>}
                                    </div>
                                </div>
                            </li>
                        </ul>
                    );
                }}
                popupIcon={<ArrowDownIcon onClick={handleOpen} />}
                noOptionsText={t('validations.noFoundAutocomplete')}
            />
            <div className="autocomplete-selectedTags">{selectedTags}</div>
            {error && (
                <div className="input-mui__error-message">
                    <ErrorIcon /> {error.message}
                </div>
            )}
        </>
    );
};

AutocompleteMUI.propTypes = {
    className: PropTypes.string,
    text: PropTypes.string,
    array: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            text: PropTypes.string,
            avatar: PropTypes.string,
            id: PropTypes.string.isRequired,
        })
    ).isRequired,
    onChange: PropTypes.func.isRequired,
    value: PropTypes.array,
};

export default AutocompleteMUI;
